import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { Config } from "src/app/config";
import { AuthService } from "@shared/services/auth.service";
import { CatService } from "@shared/services/cat.service";
import { AnnotationElement, ServiceCallInfo } from "@shared/models/common";
import { AssetImagesInfo, AssetImagesInfoDto, AssetImageViewModel, AssetDocMetadata } from "../model/image-assets.model";
import { CatDoc } from "@shared/models/cat.model";

@Injectable()
export class AssetImagesService {
    scaiServiceUrl = Config.assetConfigData['scaiServiceUrl'];

    constructor(private http: HttpClient, private authService: AuthService, private catService: CatService) {}

    getAssetImages(catId: number, pageNum: number, pageSize: number, figureType?: string, figureCategory?: string): Observable<AssetImagesInfo> {
        let url = this.scaiServiceUrl + `image-assets?cat_id=${catId}&page_num=${pageNum}&page_size=${pageSize}`;
        if (figureType) {
            url += `&figure_type=${encodeURIComponent(figureType)}`;
        }
        if (figureCategory) {
            url += `&figure_category=${encodeURIComponent(figureCategory)}`;
        }
        return this.http.get<AssetImagesInfoDto>(url, {
            headers: this.authService.getRequestHeaders(),
            withCredentials: false,
            responseType: 'json',
        }).pipe(
            map((data: AssetImagesInfoDto) => {
                const stats = { totalCount: data.totalCount };
                const assets = this.extractAssets(data.image_assets || []);
                this.fetchS3Urls(assets);
                return { imageAssets: assets, stats } as AssetImagesInfo;
            }),
            catchError((error) => this.authService.handleError(error, new ServiceCallInfo('get_asset_images', "", url)))
        );
    }

    getClassificationCategories(catId?: number, envId?: number, lang?: string): Observable<string[]> {
        let url = this.scaiServiceUrl + `image-classification-categories`;
        const params: string[] = [];
        if (catId != null) params.push(`cat_id=${encodeURIComponent(catId)}`);
        if (envId != null) params.push(`env_id=${encodeURIComponent(envId)}`);
        if (lang) params.push(`lang=${encodeURIComponent(lang)}`);
        if (params.length) url += `?${params.join('&')}`;
        return this.http.get<{ classification_categories: string[] }>(url, {
            headers: this.authService.getRequestHeaders(),
            withCredentials: false,
            responseType: 'json',
        }).pipe(
            map((res) => res.classification_categories || []),
            catchError((error) => this.authService.handleError(error, new ServiceCallInfo('get_classification_categories', "", url)))
        );
    }

    saveAssetImageMetadata(imageAssetId: number, metadataToSave: { [key: string]: string[] }) {
        let url = this.scaiServiceUrl + `image-assets/${imageAssetId}/metadata`;
        return this.http.put(url, { "metadata": metadataToSave }, {
            headers: this.authService.getRequestHeaders(),
            withCredentials: false,
            responseType: 'json',
        }).pipe(catchError((error) => this.authService.handleError(error, new ServiceCallInfo('update_asset_image_metadata', "", url))));
    }

    deleteAssetImage(imageAssetId: number) {
        let url = this.scaiServiceUrl + `image-assets/${imageAssetId}`;
        return this.http.delete(url, {
            headers: this.authService.getRequestHeaders(),
            withCredentials: false,
            responseType: 'json',
        }).pipe(catchError((error) => this.authService.handleError(error, new ServiceCallInfo('delete_asset_image', "", url))));
    }

    private extractAssets(data: any[]): AssetImageViewModel[] {
        return data.map((elem) => this.extractAsset(elem));
    }

    private extractAsset(dto: any): AssetImageViewModel {
        const fields = (dto.image_asset_fields || []).map((f: any) => ({
            imageAssetFieldId: f.image_asset_field_id,
            imageAssetId: f.image_asset_id,
            fieldName: f.field_name,
            fieldType: f.field_type,
            fieldText: f.field_text,
        }));
        const catImages = (dto.cat_images || []).map((c: any) => ({
            catVersionId: c.cat_version_id,
            catImageId: c.cat_image_id,
            documentId: c.document_id,
            documentName: c.document_name,
            versionId: c.version_id,
            fileName: c.file_name,
            pageNum: c.page_num,
            x1: c.x1,
            y1: c.y1,
            x2: c.x2,
            y2: c.y2,
            pagesCnt: c.pages_cnt,
        }));
        const docs: CatDoc[] = catImages.filter((c: any) => c.documentId).map((c: any) => {
            const d = new CatDoc();
            d.docId = c.documentId;
            d.docName = c.documentName;
            d.versionId = c.versionId;
            d.fileName = c.fileName;
            d.singlePhrasePageNum = c.pageNum != null ? c.pageNum + 1 : 1;
            d.pagesNum = c.pagesCnt;
            if (c.x1 != null && c.y1 != null && c.x2 != null && c.y2 != null) {
                let ann = new AnnotationElement();
                ann.x1 = c.x1;
                ann.y1 = c.y1;
                ann.x2 = c.x2;
                ann.y2 = c.y2;
                ann.annColor = '#ff2229';
                ann.annId = c.catImageId;
                ann.annIdPrefix = 'img';
                ann.polygon = [
                    { x: c.x1, y: c.y1, id: 0, badgeSw: false } as any,
                    { x: c.x2, y: c.y1, id: 1, badgeSw: false } as any,
                    { x: c.x2, y: c.y2, id: 2, badgeSw: false } as any,
                    { x: c.x1, y: c.y2, id: 3, badgeSw: false } as any,
                ];
                ann.borderLines = [];
                d.annotation = ann;
            }
            return d;
        });
        const metadata: AssetDocMetadata[] = fields
            .filter((f: any) => f.fieldType === 'metadata')
            .map((f: any) => ({
                fieldLabel: f.fieldName,
                fieldName: f.fieldName,
                fieldValues: f.fieldText ? f.fieldText.split('~||~') : [],
            }));
        return {
            imageAssetId: dto.image_asset_id,
            catId: dto.cat_id,
            s3Key: dto.s3_key,
            encryptKey: dto.encrypt_key,
            s3Url: null,
            figureType: dto.figure_type,
            figureSubType: dto.figure_sub_type,
            figureCategory: dto.figure_category,
            imageAssetFields: fields,
            catImages: catImages,
            docs: docs,
            metadata: metadata,
        } as AssetImageViewModel;
    }

    private fetchS3Urls(assets: AssetImageViewModel[]) {
        const keys = assets.map(a => a.s3Key).filter(k => !!k);
        if (keys.length === 0) return;
        this.catService.getS3Objects(keys).subscribe(() => {
            assets.forEach(a => {
                const presigned = (this.catService as any).presignedUrls?.get(a.s3Key) || (this.catService as any).getS3TempObjectPresignedUrl?.(a.s3Key);
                if (presigned) a.s3Url = presigned;
            });
        });
        assets.forEach(a => {
            const cached = (this.catService as any).presignedUrls?.get(a.s3Key);
            if (cached) a.s3Url = cached;
        });
    }
}
